<template>
  <section class="workpackage d-flex flex-column" :class="workpackageSetupJobStatus">
    <WorkpackagePanel
      ref="panel"
      :workpackage="item"
      :disable-expand="isWorkpackageSetupRunning"
      :is-link="isLink"
      @save:workpackage="validateAndSaveWorkpackage"
    />
    <WorkpackageScope
      v-if="isScoped"
      ref="scope"
      :is-workpackage-setup-running="isWorkpackageSetupRunning"
      @close="closeScope"
      @save:workpackage="validateAndSaveWorkpackage"
      @run-workpackage-setup="runWorkpackageSetup"
    />
    <WorkpackageScenarios v-if="isWPScenariosExpanded" class="pl-5" />
    <DependencyTreeFeedbackModal
      :value="dependencyTreeModalOpen"
      :results="dependencyTreeFeedback"
      page="workpackageSetup"
      @close="closeDependencyTreeModal"
      @commit="commitHandler()"
    />
  </section>
</template>

<script>
import { get, includes, isEqual, pickBy } from 'lodash';
import { mapState, mapActions } from 'pinia';
import to from 'await-to-js';
import jobapi from '@enums/jobapi';

// Stores
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useBundlesStore } from '@/stores/bundles';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useSnackbarStore } from '@/stores/snackbar';

export default {
  props: {
    item: {
      type: Object,
      required: true,
    },

    isLink: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      dependencyTreeModalOpen: false,
      dependencyTreeFeedback: {},
      calledFromSetup: false,
      isRunWorkpackageSetupInProgress: false,
    };
  },

  computed: {
    ...mapState(useBundlesStore, ['selectedBundle']),
    ...mapState(useWorkpackagesStore, [
      'isWPSetupExpanded',
      'isScenarioPanelExpanded',
      'selectedWorkpackage',
      'activeWorkpackages',
      'savedWorkpackage',
    ]),

    workpackageSetupJobStatus() {
      if (this.item.jobs && this.item.jobs.workpackageSetup) {
        return this.item.jobs.workpackageSetup.status.toLowerCase();
      }
      return '';
    },

    isWorkpackageSetupRunning() {
      return (
        (this.workpackageSetupJobStatus
          ? !includes(jobapi.jobFinishedStatuses, this.workpackageSetupJobStatus)
          : false) || this.isRunWorkpackageSetupInProgress
      );
    },

    isWPScenariosExpanded() {
      return this.isScenarioPanelExpanded && this.selectedWorkpackage._id === this.item._id;
    },

    isScoped() {
      return this.isWPSetupExpanded && this.selectedWorkpackage._id === this.item._id;
    },
  },

  methods: {
    ...mapActions(useWorkpackagesStore, [
      'updateWorkpackage',
      'fetchSingleWorkpackage',
      'fetchWorkpackages',
      'deleteWorkpackageById',
      'toggleWorkpackageSetupExpanded',
      'updateWorkpackageJobDetails',
      'runWorkpackageSetupJob',
      'clearWpSetupChanges',
    ]),
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),
    ...mapActions(useSnackbarStore, ['showSnackbar']),

    async commitHandler() {
      const workpackage = this.getData();
      if (!this.validate(workpackage)) {
        return;
      }

      if (this.calledFromSetup) await this.runWorkpackageSetup(true);
      else this.saveWorkpackage(workpackage, true);

      this.closeDependencyTreeModal();
    },

    async runWorkpackageSetup(commit = false) {
      this.isRunWorkpackageSetupInProgress = true;

      try {
        this.calledFromSetup = true;
        const workpackage = this.getData();
        if (!this.validate(workpackage)) {
          return;
        }

        const needsFeedback = await this.saveWorkpackage(
          workpackage,
          commit,
          'workpackageSetupRerun'
        );
        if (needsFeedback) {
          return;
        }

        await this.runWorkpackageSetupJob();
        await this.fetchWorkpackages({
          bundleId: get(this.selectedBundle, '_id'),
          shouldShowSpinner: false,
        }); // with the spinner item will jump
      } finally {
        this.isRunWorkpackageSetupInProgress = false;
      }
    },

    async validateAndSaveWorkpackage(commit) {
      const data = this.getData();

      if (!this.validate(data)) {
        return;
      }
      await this.saveWorkpackage(data, commit);
    },

    async saveWorkpackage(data, commit = false, changeType = 'workpackageSetupModified') {
      // The data we receive here will contail the entire object being modified, instead of the data diff.
      // So we pick the difference to validate on the dependency tree changes.
      const changedData = pickBy(data, (val, field) => {
        const savedVal = this.savedWorkpackage[field];
        return !isEqual(val, savedVal);
      });
      const results = await this.triggerDependencyTree({
        params: {
          change: changeType,
          updates: { global: changedData },
          commit,
          workpackageId: this.item._id,
        },
      });

      if (results.needsFeedback) {
        this.dependencyTreeFeedback = results.output;
        this.dependencyTreeModalOpen = true;
        return true;
      }

      const [err] = await to(
        this.updateWorkpackage({ workpackageId: this.item._id, updates: data })
      );

      await this.fetchSingleWorkpackage({
        workpackageId: this.item._id,
        includeAssortmentGroups: true,
      });
      if (err) {
        const message = (err && err.message) || this.$t('errors.generalErrorMessage');
        this.showError(message);
      }
    },

    showError(content) {
      this.showSnackbar({ content });
    },

    getData() {
      return {
        _id: this.item._id,
        ...this.getPanelPayload(),
        ...this.getScopePayload(),
      };
    },

    getPanelPayload() {
      const model = this.$refs.panel.getData();
      const data = {};
      ['name', 'description', 'type', 'targetLaunchDate', 'businessUnit'].forEach(key => {
        data[key] = model[key];
      });
      return data;
    },

    getScopePayload() {
      const scopeRes = this.$refs.scope ? this.$refs.scope.getData() : {};
      return scopeRes;
    },

    validateName(data) {
      if (data._id) {
        return true;
      }
      const exist = this.activeWorkpackages.some(workpackage => data.name === workpackage.name);
      if (!exist) {
        return true;
      }

      this.showError(this.$t('workpackagePage.validation.name.unique'));
      return false;
    },

    validateScope() {
      if (this.$refs.scope) {
        return this.$refs.scope.validate();
      }
      return true;
    },

    validate(data) {
      return this.$refs.panel.validate() && this.validateScope() && this.validateName(data);
    },

    closeScope() {
      this.clearWpSetupChanges();
      this.toggleWorkpackageSetupExpanded({ workpackage: this.item });
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
      this.calledFromSetup = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.workpackage {
  border-top: 1px solid $workpackage-top-border;

  &.failed {
    border-top: $assortment-workpackage-drag-cell-colour-failed 1px solid !important;
  }

  &.finished {
    border-top: $assortment-workpackage-drag-cell-colour-finished 1px solid !important;
  }

  &__setup-date-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__modified-date {
    padding-right: 3px;
    padding-bottom: 2px;
    font-weight: bold;
    width: 68px;
  }

  &__setup-icons {
    display: flex;
    align-items: center;
  }

  &__success {
    margin-right: 7px;
  }

  &__cell-users {
    text-align: right;
  }

  &__cell-users-permissions-icons {
    display: flex;
    justify-content: flex-start;

    :deep(.v-avatar),
    :deep(svg) {
      margin-right: 4px;
    }
  }

  &__cell-users-permissions-title {
    color: $assortment-primary-colour;
    text-align: left;
  }

  &__cell-icons {
    :deep(.v-icon:not(:last-child)) {
      margin-right: 7px;
      color: $assortment-primary-colour;
    }
  }

  .unbolded-header {
    font-weight: normal;
  }

  :deep(.v-select__selection.v-select__selection--comma) {
    font-weight: bold;
  }
}
</style>
